import type { ReactNode } from 'react';

import { Wordmark } from '../components/brand/Wordmark.js';

/**
 * Centred, single-card layout for unauthenticated pages (login, MFA
 * setup/verify, password reset, recovery code). No sidebar, no
 * top-nav — the only navigation that exists at this stage is the
 * route the page itself sets.
 *
 * Background: `bg-glacier` (#EEF4F8).
 * Card: surface white, border platinum, soft card shadow, max-w-md.
 * Header: plain "CRM" wordmark at xl weight.
 * Footer: discreet "Powered by DGITAL.CH" attribution link.
 *
 * Use:
 *
 *   <AuthLayout title="Sign in" subtitle="Welcome back">
 *     <LoginForm />
 *   </AuthLayout>
 */
type Props = {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /**
   * Extra content rendered ABOVE the title — used by multi-step pages
   * (e.g. MFA-setup) to display a step indicator or back link.
   */
  beforeTitle?: ReactNode;
  /**
   * Extra content rendered BELOW the card — used by login/reset pages
   * to surface cross-links (forgot password, contact admin).
   */
  belowCard?: ReactNode;
};

export function AuthLayout({ title, subtitle, children, beforeTitle, belowCard }: Props) {
  return (
    <main className="min-h-screen flex flex-col bg-glacier">
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Wordmark size="md" />
          </div>

          <div className="card p-8">
            {beforeTitle ? <div className="mb-4">{beforeTitle}</div> : null}
            <h2 className="font-display text-xl font-semibold text-ink tracking-tight">{title}</h2>
            {subtitle ? <div className="mt-2 text-sm text-ink-secondary">{subtitle}</div> : null}
            <div className="mt-6">{children}</div>
          </div>

          {belowCard ? <div className="mt-6">{belowCard}</div> : null}
        </div>
      </div>

      <footer className="py-6 text-center text-xs text-ink-tertiary">
        Powered by{' '}
        <a
          href="https://dgital.ch"
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink-secondary hover:underline"
        >
          DGITAL.CH
        </a>
      </footer>
    </main>
  );
}
